import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Redirect, useHistory } from 'react-router-dom';
import {
  syncSubscriptionState,
  activateTrial,
  subscribeToPlan,
  cancelSubscription,
} from '../../store/actions/subscriptionActions';
import {
  hasAppAccess,
  isTrialAccess,
  isTrialExpired,
  isCancelPending,
  canStartTrial,
  formatPlanPrice,
  formatDuration,
  formatEndDate,
  daysRemaining,
  FREE_TRIAL_PLAN_NAME,
} from '../../helpers/subscription.helpers';
import { GST_STATES, normaliseGstin, isValidGstin, formatInr } from '../../helpers/gst.helpers';
import { fetchInvoices, downloadInvoice } from '../../services/subscriptionService';
import '../start/PasswordGate.css';
import './SubscriptionPage.css';

/** Confirmation for a cancellation, which is not undoable from this screen. */
const CancelDialog = ({ planName, until, busy, onKeep, onConfirm }) => (
  <div className="xk-sub-modal__overlay" role="presentation" onClick={busy ? undefined : onKeep}>
    <div
      className="xk-sub-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="xk-sub-cancel-title"
      onClick={(e) => e.stopPropagation()}
    >
      <h3 className="xk-sub-modal__title" id="xk-sub-cancel-title">
        Cancel {planName}?
      </h3>
      <p className="xk-sub-modal__text">
        {until
          ? `You keep full access until ${until}. Nothing further will be charged, and the subscription stops at the end of the period you have already paid for.`
          : 'Nothing further will be charged, and the subscription stops at the end of the period you have already paid for.'}
      </p>
      <div className="xk-sub-modal__actions">
        <button type="button" className="xk-sub-btn" disabled={busy} onClick={onKeep}>
          Keep subscription
        </button>
        <button
          type="button"
          className="xk-sub-btn xk-sub-btn--danger"
          disabled={busy}
          onClick={onConfirm}
        >
          {busy ? 'Cancelling…' : 'Cancel subscription'}
        </button>
      </div>
    </div>
  </div>
);

CancelDialog.propTypes = {
  planName: PropTypes.string,
  until: PropTypes.string,
  busy: PropTypes.bool,
  onKeep: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

CancelDialog.defaultProps = { planName: 'your subscription', until: '', busy: false };

/** Price as "₹999 + 18% GST", with the amount actually charged underneath. */
const PlanPrice = ({ plan }) => {
  if (!plan.gstPercent || !plan.taxableInr || plan.taxableInr === plan.amountInr) {
    return <div className="xk-sub-plan__price">{formatPlanPrice(plan)}</div>;
  }
  return (
    <>
      <div className="xk-sub-plan__price">
        {formatInr(plan.taxableInr)}
        <span className="xk-sub-plan__gst"> + {plan.gstPercent}% GST</span>
      </div>
      <div className="xk-sub-plan__total">Total {formatInr(plan.amountInr)} incl. GST</div>
    </>
  );
};

PlanPrice.propTypes = { plan: PropTypes.object.isRequired };

const SubscriptionPage = ({
  isAuthenticated,
  user,
  bootstrapped,
  subscription,
  access,
  trialDays,
  plans,
  loading,
  error,
  synced,
  syncSubscriptionStateAction,
  activateTrialAction,
  subscribeToPlanAction,
  cancelSubscriptionAction,
}) => {
  const history = useHistory();
  const [localError, setLocalError] = React.useState('');
  const [busyPlanId, setBusyPlanId] = React.useState(null);
  const [confirmingCancel, setConfirmingCancel] = React.useState(false);
  const [cancelBusy, setCancelBusy] = React.useState(false);
  const [notice, setNotice] = React.useState('');
  const [billing, setBilling] = React.useState({ name: '', gstin: '', stateCode: '', address: '', city: '' });
  const [billingTouched, setBillingTouched] = React.useState(false);
  const [invoices, setInvoices] = React.useState([]);

  const savedBilling = subscription && subscription.billing;
  React.useEffect(() => {
    if (billingTouched) return;
    setBilling({
      name: (savedBilling && savedBilling.name) || (user && (user.organization || user.name)) || '',
      gstin: (savedBilling && savedBilling.gstin) || '',
      stateCode: (savedBilling && savedBilling.stateCode) || '',
      address: (savedBilling && savedBilling.address) || '',
      city: (savedBilling && savedBilling.city) || '',
    });
  }, [savedBilling, user, billingTouched]);

  const accessActive = Boolean(access && access.active);
  React.useEffect(() => {
    if (!isAuthenticated || !accessActive) return;
    fetchInvoices()
      .then(setInvoices)
      .catch(() => setInvoices([]));
  }, [isAuthenticated, accessActive]);

  React.useEffect(() => {
    if (isAuthenticated) {
      syncSubscriptionStateAction().catch(() => {});
    }
  }, [isAuthenticated, syncSubscriptionStateAction]);

  if (!bootstrapped || (loading && !synced)) {
    return (
      <div className="xk-gate xk-sub-page">
        <div className="xk-gate__card xk-sub-card">
          <div className="xk-sub-loading">
            <div className="spinner-border text-success" role="status" />
            <p className="mt-2 text-muted mb-0">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Redirect to="/login" />;
  }

  // /subscription always stays on Subscription — do not bounce to Home from prior access.
  const hasAccess = synced && access && hasAppAccess(access);
  const onTrial = hasAccess && isTrialAccess(access);
  const cancelPending = isCancelPending({ access, subscription });
  const paidActive = hasAccess && !onTrial;

  const showTrialOffer = !hasAccess && canStartTrial(subscription);
  const trialExpired = synced && !hasAccess && isTrialExpired({ subscription, access });
  // A trial can be upgraded, and a winding-down subscription can be replaced;
  // only a paid subscription that is still renewing has nothing to buy.
  const showPlans = !paidActive || cancelPending;
  const showCancel = paidActive && !cancelPending;

  const currentPlan =
    (Array.isArray(plans) && plans.find((p) => p.id === (access && access.planCode))) || null;
  const planName = onTrial ? FREE_TRIAL_PLAN_NAME : (currentPlan && currentPlan.name) || 'Your plan';
  const endsAt = (access && access.endsAt) || (subscription && subscription.endsAt);
  const trialEndedAt = subscription && subscription.trialEnds;
  const trialEndedOn = formatEndDate(trialEndedAt);
  const until = formatEndDate(endsAt);
  const daysLeft = daysRemaining(endsAt);

  const goHome = () => {
    history.push('/');
  };

  const handleTrial = async () => {
    setLocalError('');
    try {
      await activateTrialAction();
      history.push('/');
    } catch (e) {
      setLocalError(e.message || 'Could not start free trial');
    }
  };

  const updateBilling = (field, value) => {
    setBillingTouched(true);
    setBilling((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'gstin') {
        const gstin = normaliseGstin(value);
        next.gstin = gstin;
        if (isValidGstin(gstin)) next.stateCode = gstin.slice(0, 2);
      }
      return next;
    });
  };

  const handlePlan = async (planId) => {
    setLocalError('');
    setNotice('');
    const gstin = normaliseGstin(billing.gstin);
    if (gstin && !isValidGstin(gstin)) {
      setLocalError('Enter a valid 15-character GSTIN, or leave it blank.');
      return;
    }
    if (!billing.city.trim()) {
      setLocalError('Enter your city for the GST invoice.');
      return;
    }
    if (!billing.stateCode) {
      setLocalError('Select your state for the GST invoice.');
      return;
    }
    setBusyPlanId(planId);
    try {
      await subscribeToPlanAction(planId, { ...billing, gstin });
      history.push('/');
    } catch (e) {
      if (e.message !== 'Payment cancelled') {
        setLocalError(e.message || 'Could not complete payment');
      }
    } finally {
      setBusyPlanId(null);
    }
  };

  const handleCancel = async () => {
    setLocalError('');
    setCancelBusy(true);
    try {
      await cancelSubscriptionAction();
      setConfirmingCancel(false);
      setNotice(
        until
          ? `Subscription cancelled. You keep full access until ${until}.`
          : 'Subscription cancelled.'
      );
    } catch (e) {
      setLocalError(e.message || 'Could not cancel the subscription');
    } finally {
      setCancelBusy(false);
    }
  };

  const displayError = localError || error;

  let lead;
  if (paidActive) {
    lead = cancelPending
      ? 'Your subscription is winding down. Start a new one whenever you are ready.'
      : 'Your subscription and billing, in one place.';
  } else if (onTrial) {
    lead = `You are on the ${FREE_TRIAL_PLAN_NAME} — subscribe to unlock Horseshoe, Circular and Shaft.`;
  } else if (showTrialOffer) {
    lead = `Start the ${FREE_TRIAL_PLAN_NAME} to try D-Shape plotting and orientation, or choose a paid plan for full access.`;
  } else if (trialExpired) {
    lead = '';
  } else {
    lead = 'Select a subscription plan to access XLog3D engineering tools.';
  }

  let pageTitle = 'Choose Your Access';
  if (hasAccess) pageTitle = 'Manage Subscription';
  else if (trialExpired) pageTitle = `${FREE_TRIAL_PLAN_NAME} has ended`;

  return (
    <div
      className={`xk-gate xk-sub-page${
        showTrialOffer ? ' xk-sub-page--trial-offer' : ''
      }${trialExpired ? ' xk-sub-page--trial-ended' : ''}`}
    >
      <div className="xk-gate__card xk-sub-card">
        <img
          src={`${process.env.PUBLIC_URL || ''}/XLog3D Logo_Red.png`}
          alt="XLog3D"
          className="xk-gate__logo"
        />

        <h2 className="xk-gate__title">{pageTitle}</h2>
        {lead && <p className="xk-sub-lead">{lead}</p>}

        {trialExpired && (
          <div className="xk-sub-trial-ended" role="status">
            <div className="xk-sub-trial-ended__icon" aria-hidden="true">
              <i className="fa fa-clock-o" />
            </div>
            <p className="xk-sub-trial-ended__text">
              {trialEndedOn
                ? `Your ${trialDays}-day ${FREE_TRIAL_PLAN_NAME} ended on ${trialEndedOn}.`
                : `Your ${FREE_TRIAL_PLAN_NAME} has ended.`}{' '}
              Subscribe below to unlock full access to D-Shape, Horseshoe, Circular and Shaft modules.
            </p>
          </div>
        )}

        {hasAccess && (
          <div className={`xk-sub-current ${cancelPending ? 'xk-sub-current--ending' : ''}`}>
            <div className="xk-sub-current__head">
              <span className="xk-sub-current__name">{planName}</span>
              <span
                className={`xk-sub-current__badge ${
                  cancelPending ? 'xk-sub-current__badge--ending' : ''
                }`}
              >
                {cancelPending ? 'Ending' : onTrial ? 'Limited' : 'Active'}
              </span>
            </div>

            <div className="xk-sub-current__meta">
              {onTrial && until && (
                <>
                  {daysLeft === 0
                    ? 'Ends today'
                    : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}
                  {' · until '}
                  {until}
                </>
              )}
              {!onTrial && until && (cancelPending ? `Access until ${until}` : `Valid until ${until}`)}
              {!until && 'Active'}
            </div>

            {paidActive && invoices.length > 0 && (
              <div className="xk-sub-invoices">
                <div className="xk-sub-invoices__title">GST invoices</div>
                {invoices.map((inv) => (
                  <div key={inv.id} className="xk-sub-invoices__row">
                    <span>
                      {inv.invoiceNumber} · {formatEndDate(inv.issueDate)} · {formatInr(inv.total)}
                    </span>
                    <button
                      type="button"
                      className="xk-sub-btn xk-sub-btn--link"
                      onClick={() =>
                        downloadInvoice(inv).catch((e) =>
                          setLocalError(e.message || 'Could not download the invoice')
                        )
                      }
                    >
                      Download PDF
                    </button>
                  </div>
                ))}
              </div>
            )}

            {cancelPending && (
              <p className="xk-sub-current__note">
                Cancellation is scheduled. You keep full access until the end of the period you have
                already paid for, and nothing further will be charged.
              </p>
            )}

            <div className="xk-sub-current__actions">
              <button type="button" className="xk-sub-btn xk-sub-btn--primary" onClick={goHome}>
                Continue to App
              </button>
              {showCancel && (
                <button
                  type="button"
                  className="xk-sub-btn xk-sub-btn--link"
                  disabled={loading || cancelBusy}
                  onClick={() => setConfirmingCancel(true)}
                >
                  Cancel subscription
                </button>
              )}
            </div>
          </div>
        )}

        {notice && <div className="xk-sub-notice">{notice}</div>}

        {showTrialOffer && (
          <div className="xk-sub-trial xk-sub-trial--featured">
            <div className="xk-sub-trial__header">
              <span className="xk-sub-trial__badge xk-sub-trial__badge--trial">Limited Features</span>
            </div>
            <h3 className="xk-sub-trial__title">{trialDays}-Day {FREE_TRIAL_PLAN_NAME}</h3>
            <p className="xk-sub-trial__text">
              Get D-Shape plotting and orientation for {trialDays} days with watermarked output. One trial per account.
            </p>
            <button
              type="button"
              className="xk-sub-trial__btn xk-sub-trial__btn--primary"
              disabled={loading}
              onClick={handleTrial}
            >
              {loading && !busyPlanId ? 'Starting trial...' : 'Start Free Trial'}
            </button>
          </div>
        )}

        {showPlans && plans.length > 0 && (
          <div className="xk-sub-divider">
            <span>
              {onTrial
                ? 'upgrade to a paid plan'
                : cancelPending
                ? 'start a new subscription'
                : showTrialOffer
                ? 'or subscribe'
                : trialExpired
                ? 'subscribe to continue'
                : 'choose a plan'}
            </span>
          </div>
        )}

        {showPlans && plans.length > 0 && (
          <div className="xk-sub-billing">
            <div className="xk-sub-billing__title">Details for your GST invoice</div>
            <label className="xk-sub-billing__field">
              <span>Name / Company on invoice</span>
              <input
                type="text"
                value={billing.name}
                maxLength={120}
                onChange={(e) => updateBilling('name', e.target.value)}
              />
            </label>
            <label className="xk-sub-billing__field">
              <span>Address</span>
              <input
                type="text"
                value={billing.address}
                maxLength={200}
                placeholder="House / street / area"
                onChange={(e) => updateBilling('address', e.target.value)}
              />
            </label>
            <label className="xk-sub-billing__field">
              <span>City *</span>
              <input
                type="text"
                value={billing.city}
                maxLength={80}
                onChange={(e) => updateBilling('city', e.target.value)}
              />
            </label>
            <label className="xk-sub-billing__field">
              <span>State *</span>
              <select
                value={billing.stateCode}
                onChange={(e) => updateBilling('stateCode', e.target.value)}
                disabled={isValidGstin(normaliseGstin(billing.gstin))}
              >
                <option value="">Select state</option>
                {GST_STATES.map(([code, name]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
            <label className="xk-sub-billing__field">
              <span>GSTIN (optional)</span>
              <input
                type="text"
                value={billing.gstin}
                maxLength={15}
                placeholder="15-character GSTIN, if registered"
                onChange={(e) => updateBilling('gstin', e.target.value)}
              />
            </label>
          </div>
        )}

        <div className="xk-sub-plans">
          {showPlans &&
            plans.map((plan) => (
              <div key={plan.id} className="xk-sub-plan">
                <div className="xk-sub-plan__head">
                  <h3 className="xk-sub-plan__name">{plan.name}</h3>
                  <PlanPrice plan={plan} />
                  <div className="xk-sub-plan__duration">{formatDuration(plan.durationMonths)}</div>
                </div>
                <ul className="xk-sub-plan__features">
                  <li>Full engineering module access</li>
                  <li>D-Shape, Horseshoe, Circular &amp; Shaft</li>
                  <li>Export &amp; plotting tools</li>
                </ul>
                <button
                  type="button"
                  className="xk-gate__submit xk-sub-plan__btn"
                  disabled={loading}
                  onClick={() => handlePlan(plan.id)}
                >
                  {busyPlanId === plan.id ? 'Opening payment...' : 'Subscribe'}
                </button>
              </div>
            ))}
        </div>

        {showPlans && !loading && plans.length === 0 && !showTrialOffer && (
          <p className="xk-sub-empty">No plans available. Please contact support.</p>
        )}

        {displayError && <div className="xk-gate__error">{displayError}</div>}

        {loading && plans.length === 0 && (
          <div className="xk-sub-loading">
            <div className="spinner-border text-success" role="status" />
            <p className="mt-2 text-muted mb-0">Loading plans...</p>
          </div>
        )}
      </div>

      {confirmingCancel && (
        <CancelDialog
          planName={planName}
          until={until}
          busy={cancelBusy}
          onKeep={() => setConfirmingCancel(false)}
          onConfirm={handleCancel}
        />
      )}
    </div>
  );
};

SubscriptionPage.propTypes = {
  isAuthenticated: PropTypes.bool,
  user: PropTypes.object,
  bootstrapped: PropTypes.bool,
  subscription: PropTypes.object,
  access: PropTypes.object,
  trialDays: PropTypes.number,
  plans: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  error: PropTypes.string,
  synced: PropTypes.bool,
  syncSubscriptionStateAction: PropTypes.func.isRequired,
  activateTrialAction: PropTypes.func.isRequired,
  subscribeToPlanAction: PropTypes.func.isRequired,
  cancelSubscriptionAction: PropTypes.func.isRequired,
};

SubscriptionPage.defaultProps = {
  isAuthenticated: false,
  user: null,
  bootstrapped: false,
  subscription: null,
  access: null,
  trialDays: 21,
  plans: [],
  loading: false,
  error: null,
  synced: false,
};

const mapStateToProps = (state) => ({
  isAuthenticated: state.auth.isAuthenticated,
  user: state.auth.user,
  bootstrapped: state.auth.bootstrapped,
  subscription: state.subscription.subscription,
  access: state.subscription.access,
  trialDays: state.subscription.trialDays,
  plans: state.subscription.plans,
  loading: state.subscription.loading,
  error: state.subscription.error,
  synced: state.subscription.synced,
});

const mapDispatchToProps = {
  syncSubscriptionStateAction: syncSubscriptionState,
  activateTrialAction: activateTrial,
  subscribeToPlanAction: subscribeToPlan,
  cancelSubscriptionAction: cancelSubscription,
};

export default connect(mapStateToProps, mapDispatchToProps)(SubscriptionPage);
